import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect, type SelectOption } from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { paymentModeService, coaLedgerService } from 'api/services';
import type { ChargeRuleDto, SaveChargeRuleRequest, PaymentModeDto, SavePaymentModeRequest, ChargeRateStatDto, SettlementMode } from 'api/types';

// ============================================================================
// CHARGE RULES (ADR-0055 §6) — what the bank / gateway is expected to deduct per
// payment mode: a percentage and/or fixed amount (min/max), GST on top, a
// tolerance, and single vs batch settlement (optionally from a clearing account).
// A rule on "All bank accounts" is the entity default; a rule on this account
// overrides it. The reconciliation suggests matches with these; the user confirms
// the actual deduction. Also: the rate actually paid, and the payment-mode list.
// ============================================================================

const muted: React.CSSProperties = { color: 'var(--text-muted)' };
const th: React.CSSProperties = { textAlign: 'left', padding: '0.45rem 0.5rem' };
const td: React.CSSProperties = { padding: '0.35rem 0.5rem', verticalAlign: 'top' };
const labelStyle: React.CSSProperties = { fontSize: '0.8125rem', marginBottom: '0.25rem', display: 'block', color: 'var(--text-secondary)', fontWeight: 500 };
const inputStyle: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.375rem 0.625rem' };
const num = (s: string) => { const n = parseFloat(s); return isNaN(n) ? 0 : n; };
const optNum = (s: string) => (s.trim() === '' ? null : num(s));
const fmt = (n?: number | null, dp = 2) => (n == null ? '' : n.toLocaleString(undefined, { maximumFractionDigits: dp }));

interface RuleForm {
  id: string | null;
  rowVersion: number;
  paymentModeId: string;
  scope: 'BANK' | 'ALL';
  chargePercent: string; chargeFixed: string; chargeMin: string; chargeMax: string; gstPercent: string;
  chargesLedgerId: string; chargesLedgerName: string; gstLedgerId: string; gstLedgerName: string;
  toleranceAmount: string; tolerancePercent: string;
  settlementMode: SettlementMode; settlementDays: string;
  sourceLedgerId: string; sourceLedgerName: string;
  notes: string;
}

const blankRule = (): RuleForm => ({
  id: null, rowVersion: 0, paymentModeId: '', scope: 'BANK',
  chargePercent: '', chargeFixed: '', chargeMin: '', chargeMax: '', gstPercent: '',
  chargesLedgerId: '', chargesLedgerName: '', gstLedgerId: '', gstLedgerName: '',
  toleranceAmount: '0.50', tolerancePercent: '', settlementMode: 'SINGLE', settlementDays: '2',
  sourceLedgerId: '', sourceLedgerName: '', notes: '',
});

const toForm = (r: ChargeRuleDto): RuleForm => ({
  id: r.id, rowVersion: r.rowVersion, paymentModeId: r.paymentModeId || '', scope: r.bankLedgerId ? 'BANK' : 'ALL',
  chargePercent: r.chargePercent ? String(r.chargePercent) : '', chargeFixed: r.chargeFixed ? String(r.chargeFixed) : '',
  chargeMin: r.chargeMin != null ? String(r.chargeMin) : '', chargeMax: r.chargeMax != null ? String(r.chargeMax) : '',
  gstPercent: r.gstPercent != null ? String(r.gstPercent) : '',
  chargesLedgerId: r.chargesLedgerId || '', chargesLedgerName: r.chargesLedgerName || '',
  gstLedgerId: r.gstLedgerId || '', gstLedgerName: r.gstLedgerName || '',
  toleranceAmount: String(r.toleranceAmount ?? 0), tolerancePercent: r.tolerancePercent ? String(r.tolerancePercent) : '',
  settlementMode: r.settlementMode, settlementDays: String(r.settlementDays ?? 2),
  sourceLedgerId: r.sourceLedgerId || '', sourceLedgerName: r.sourceLedgerName || '', notes: r.notes || '',
});

export const describeCharge = (r: Pick<ChargeRuleDto, 'chargePercent' | 'chargeFixed' | 'chargeMin' | 'chargeMax' | 'gstPercent'>) => {
  const parts: string[] = [];
  if (r.chargePercent) parts.push(`${fmt(r.chargePercent, 4)}%`);
  if (r.chargeFixed) parts.push(fmt(r.chargeFixed));
  let s = parts.length ? parts.join(' + ') : 'No charge';
  if (r.chargeMin != null) s += `, min ${fmt(r.chargeMin)}`;
  if (r.chargeMax != null) s += `, max ${fmt(r.chargeMax)}`;
  if (r.gstPercent) s += ` + GST ${fmt(r.gstPercent)}%`;
  return s;
};

/** Async ledger options; remembers each label so a picked id can show its name. */
export const useLedgerLoader = (cache: React.MutableRefObject<Map<string, string>>, nature?: string) => useCallback(async (input: string): Promise<SelectOption[]> => {
  try {
    // General-ledger accounts only: a party's own ledger is reached through the party (receipt / payment vouchers).
    const res = await coaLedgerService.getAll({ pageSize: 50, searchTerm: input || undefined, ledgerKind: 'GL', ...(nature ? { ledgerNature: nature } : {}) });
    return res.data.map(l => {
      const label = `${l.ledgerCode} - ${l.ledgerName}`;
      cache.current.set(l.id, label);
      return { value: l.id, label };
    });
  } catch {
    return [];
  }
}, [cache, nature]);

export interface ChargeRulesModalProps {
  isOpen: boolean;
  bankLedgerId: string;
  bankLedgerName?: string | null;
  canUpdate: boolean;
  onChanged?: () => void;
  onClose: () => void;
}

export const ChargeRulesModal: React.FC<ChargeRulesModalProps> = ({ isOpen, bankLedgerId, bankLedgerName, canUpdate, onChanged, onClose }) => {
  const [tab, setTab] = useState<'RULES' | 'MODES'>('RULES');
  const [rules, setRules] = useState<ChargeRuleDto[]>([]);
  const [modes, setModes] = useState<PaymentModeDto[]>([]);
  const [stats, setStats] = useState<ChargeRateStatDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<RuleForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteRule, setDeleteRule] = useState<ChargeRuleDto | null>(null);
  const [modeForm, setModeForm] = useState<(SavePaymentModeRequest & { id: string | null; keywordsText: string }) | null>(null);

  const ledgerLabels = useRef<Map<string, string>>(new Map());
  const loadAnyLedger = useLedgerLoader(ledgerLabels);
  const loadBankLedger = useLedgerLoader(ledgerLabels, 'BANK');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, m, s] = await Promise.all([
        paymentModeService.getRules(bankLedgerId),
        paymentModeService.getModes(true),
        paymentModeService.getChargeStats(bankLedgerId, 90).catch(() => [] as ChargeRateStatDto[]),
      ]);
      setRules(r); setModes(m); setStats(s);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load the charge rules.');
    } finally {
      setLoading(false);
    }
  }, [bankLedgerId]);

  useEffect(() => {
    if (!isOpen) return;
    setForm(null); setModeForm(null); setTab('RULES');
    load();
  }, [isOpen, load]);

  const modeOptions: SelectOption[] = useMemo(() => [
    { value: '', label: 'Any mode without its own rule' },
    ...modes.map(m => ({ value: m.id, label: `${m.modeName} (${m.modeCode})` })),
  ], [modes]);

  const upd = (patch: Partial<RuleForm>) => setForm(prev => (prev ? { ...prev, ...patch } : prev));

  const handleSaveRule = useCallback(async () => {
    if (!form) return;
    const payload: SaveChargeRuleRequest = {
      paymentModeId: form.paymentModeId || null,
      bankLedgerId: form.scope === 'BANK' ? bankLedgerId : null,
      chargePercent: num(form.chargePercent), chargeFixed: num(form.chargeFixed),
      chargeMin: optNum(form.chargeMin), chargeMax: optNum(form.chargeMax), gstPercent: optNum(form.gstPercent),
      chargesLedgerId: form.chargesLedgerId || null, gstLedgerId: form.gstLedgerId || null,
      toleranceAmount: num(form.toleranceAmount), tolerancePercent: num(form.tolerancePercent),
      settlementMode: form.settlementMode, settlementDays: Math.max(0, Math.round(num(form.settlementDays))),
      sourceLedgerId: form.settlementMode === 'BATCH' ? form.sourceLedgerId || null : null,
      notes: form.notes.trim() || null, rowVersion: form.rowVersion,
    };
    setSaving(true);
    try {
      await paymentModeService.saveRule(form.id, payload);
      notificationService.toast.success('Charge rule saved.');
      setForm(null);
      await load();
      onChanged?.();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not save the charge rule.');
    } finally {
      setSaving(false);
    }
  }, [form, bankLedgerId, load, onChanged]);

  const handleDeleteRule = useCallback(async () => {
    if (!deleteRule) return;
    setSaving(true);
    try {
      await paymentModeService.deleteRule(deleteRule.id);
      notificationService.toast.success('Charge rule deleted.');
      setDeleteRule(null);
      await load();
      onChanged?.();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not delete the rule.');
    } finally {
      setSaving(false);
    }
  }, [deleteRule, load, onChanged]);

  const handleSaveMode = useCallback(async () => {
    if (!modeForm) return;
    setSaving(true);
    try {
      await paymentModeService.saveMode(modeForm.id, {
        modeCode: modeForm.modeCode, modeName: modeForm.modeName,
        narrationKeywords: modeForm.keywordsText.split(',').map(s => s.trim()).filter(Boolean),
        isCash: modeForm.isCash, displayOrder: modeForm.displayOrder, description: modeForm.description || null, rowVersion: modeForm.rowVersion,
      });
      notificationService.toast.success('Payment mode saved.');
      setModeForm(null);
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not save the payment mode.');
    } finally {
      setSaving(false);
    }
  }, [modeForm, load]);

  if (!isOpen) return null;

  const ruleEditor = form && (
    <div style={{ background: 'var(--bg-secondary)', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '0.75rem' }}>
      <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>{form.id ? 'Edit charge rule' : 'New charge rule'}</div>
      <div className="form-grid-4">
        <div>
          <SyutSelect label="Payment mode" options={modeOptions} value={form.paymentModeId} menuPortalTarget={document.body}
            onChange={(v) => upd({ paymentModeId: (v as string) || '' })} isClearable={false} size="sm" />
        </div>
        <div>
          <label style={labelStyle}>Applies to</label>
          <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.8125rem', paddingTop: '0.35rem' }}>
            <label><input type="radio" checked={form.scope === 'BANK'} onChange={() => upd({ scope: 'BANK' })} /> {bankLedgerName || 'This account'}</label>
            <label><input type="radio" checked={form.scope === 'ALL'} onChange={() => upd({ scope: 'ALL' })} /> All accounts</label>
          </div>
        </div>
        <div><label style={labelStyle}>Charge %</label>
          <input className="form-input" type="number" step="0.0001" value={form.chargePercent} onChange={e => upd({ chargePercent: e.target.value })} placeholder="e.g. 1.1" style={inputStyle} /></div>
        <div><label style={labelStyle}>Fixed per transaction</label>
          <input className="form-input" type="number" step="0.01" value={form.chargeFixed} onChange={e => upd({ chargeFixed: e.target.value })} placeholder="0" style={inputStyle} /></div>
        <div><label style={labelStyle}>Minimum charge</label>
          <input className="form-input" type="number" step="0.01" value={form.chargeMin} onChange={e => upd({ chargeMin: e.target.value })} placeholder="none" style={inputStyle} /></div>
        <div><label style={labelStyle}>Maximum charge</label>
          <input className="form-input" type="number" step="0.01" value={form.chargeMax} onChange={e => upd({ chargeMax: e.target.value })} placeholder="none" style={inputStyle} /></div>
        <div><label style={labelStyle}>GST on the charge %</label>
          <input className="form-input" type="number" step="0.01" value={form.gstPercent} onChange={e => upd({ gstPercent: e.target.value })} placeholder="e.g. 18" style={inputStyle} /></div>
        <div>
          <SyutSelect label="Charges ledger (expense)" isAsync loadOptions={loadAnyLedger} defaultOptions cacheOptions={false} menuPortalTarget={document.body}
            seedOptions={form.chargesLedgerId ? [{ value: form.chargesLedgerId, label: form.chargesLedgerName }] : undefined}
            value={form.chargesLedgerId} onChange={(v) => upd({ chargesLedgerId: (v as string) || '', chargesLedgerName: ledgerLabels.current.get(v as string) || '' })}
            isClearable size="sm" />
        </div>
        <div>
          <SyutSelect label="GST ledger (input tax)" isAsync loadOptions={loadAnyLedger} defaultOptions cacheOptions={false} menuPortalTarget={document.body}
            seedOptions={form.gstLedgerId ? [{ value: form.gstLedgerId, label: form.gstLedgerName }] : undefined}
            value={form.gstLedgerId} onChange={(v) => upd({ gstLedgerId: (v as string) || '', gstLedgerName: ledgerLabels.current.get(v as string) || '' })}
            isClearable size="sm" />
        </div>
        <div><label style={labelStyle}>Tolerance (amount)</label>
          <input className="form-input" type="number" step="0.01" value={form.toleranceAmount} onChange={e => upd({ toleranceAmount: e.target.value })} style={inputStyle} /></div>
        <div><label style={labelStyle}>Tolerance (% of amount)</label>
          <input className="form-input" type="number" step="0.01" value={form.tolerancePercent} onChange={e => upd({ tolerancePercent: e.target.value })} placeholder="0" style={inputStyle} /></div>
        <div>
          <label style={labelStyle}>Settlement</label>
          <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.8125rem', paddingTop: '0.35rem' }}>
            <label><input type="radio" checked={form.settlementMode === 'SINGLE'} onChange={() => upd({ settlementMode: 'SINGLE' })} /> One credit per receipt</label>
            <label><input type="radio" checked={form.settlementMode === 'BATCH'} onChange={() => upd({ settlementMode: 'BATCH' })} /> Batched</label>
          </div>
        </div>
        <div><label style={labelStyle}>Credited within (days)</label>
          <input className="form-input" type="number" value={form.settlementDays} onChange={e => upd({ settlementDays: e.target.value })} style={inputStyle} /></div>
        {form.settlementMode === 'BATCH' && (
          <div style={{ gridColumn: 'span 2' }}>
            <SyutSelect label="Receipts sit in (clearing account, optional)" isAsync loadOptions={loadBankLedger} defaultOptions cacheOptions={false} menuPortalTarget={document.body}
              seedOptions={form.sourceLedgerId ? [{ value: form.sourceLedgerId, label: form.sourceLedgerName }] : undefined}
              value={form.sourceLedgerId} onChange={(v) => upd({ sourceLedgerId: (v as string) || '', sourceLedgerName: ledgerLabels.current.get(v as string) || '' })}
              placeholder="The bank itself" isClearable size="sm" />
          </div>
        )}
        <div style={{ gridColumn: form.settlementMode === 'BATCH' ? 'span 2' : 'span 4' }}><label style={labelStyle}>Notes</label>
          <input className="form-input" type="text" value={form.notes} onChange={e => upd({ notes: e.target.value })} placeholder="e.g. MDR as per agreement dated …" style={inputStyle} /></div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.6rem' }}>
        <button className="btn btn-secondary" onClick={() => setForm(null)} disabled={saving} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSaveRule} disabled={saving} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>{saving ? 'Saving…' : 'Save rule'}</button>
      </div>
    </div>
  );

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl" style={{ minHeight: 'auto' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Charges & Payment Modes — {bankLedgerName || 'Bank account'}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <button className={`btn ${tab === 'RULES' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: '0.8125rem', padding: '0.3rem 0.8rem' }} onClick={() => setTab('RULES')}>Charge rules</button>
            <button className={`btn ${tab === 'MODES' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: '0.8125rem', padding: '0.3rem 0.8rem' }} onClick={() => setTab('MODES')}>Payment modes</button>
            {loading && <span style={{ ...muted, fontSize: '0.78rem', alignSelf: 'center' }}>Loading…</span>}
          </div>

          {tab === 'RULES' && (
            <>
              <div style={{ ...muted, fontSize: '0.78rem', marginBottom: '0.6rem' }}>
                The deduction the bank or gateway is expected to make per payment mode. Suggestions use it to match credits that arrive
                net of charges; you confirm the actual deduction, which is booked to the charges and GST ledgers. A rule for this account overrides the one for all accounts.
              </div>
              {ruleEditor}
              <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.5rem', overflow: 'auto', maxHeight: '34vh' }}>
                <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                  <thead><tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                    <th style={th}>Mode</th><th style={th}>Applies to</th><th style={th}>Expected charge</th><th style={th}>Tolerance</th>
                    <th style={th}>Settlement</th><th style={th}>Booked to</th><th style={{ ...th, width: 90 }} />
                  </tr></thead>
                  <tbody>
                    {rules.length === 0 && <tr><td colSpan={7} style={{ ...td, ...muted, textAlign: 'center', padding: '1rem' }}>No charge rules yet — credits must then match receipts exactly.</td></tr>}
                    {rules.map(r => (
                      <tr key={r.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={td}><strong>{r.modeName || 'Any mode'}</strong>{r.modeCode && <span style={muted}> · {r.modeCode}</span>}</td>
                        <td style={td}>{r.bankLedgerId ? (r.bankLedgerName || 'This account') : <span style={muted}>All accounts</span>}</td>
                        <td style={td}>{describeCharge(r)}</td>
                        <td style={td}>±{fmt(r.toleranceAmount)}{r.tolerancePercent ? ` or ${fmt(r.tolerancePercent)}%` : ''}</td>
                        <td style={td}>{r.settlementMode === 'BATCH' ? `Batched, ${r.settlementDays}d` : `Per receipt, ${r.settlementDays}d`}{r.sourceLedgerName && <div style={{ ...muted, fontSize: '0.75rem' }}>from {r.sourceLedgerName}</div>}</td>
                        <td style={{ ...td, fontSize: '0.78rem' }}>{r.chargesLedgerName || '—'}{r.gstLedgerName && <div style={muted}>GST: {r.gstLedgerName}</div>}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          {canUpdate && <>
                            <button className="btn btn-secondary" style={{ fontSize: '0.72rem', padding: '0.15rem 0.45rem' }} onClick={() => setForm(toForm(r))}><IconSystem.edit size={12} /></button>{' '}
                            <button className="btn btn-secondary" style={{ fontSize: '0.72rem', padding: '0.15rem 0.45rem' }} onClick={() => setDeleteRule(r)}><IconSystem.trash size={12} /></button>
                          </>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {canUpdate && !form && (
                <button className="btn btn-secondary" style={{ fontSize: '0.8125rem', padding: '0.35rem 0.75rem', marginTop: '0.6rem' }} onClick={() => setForm(blankRule())}>
                  <IconSystem.add size={14} /> Add rule
                </button>
              )}

              <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', margin: '1rem 0 0.35rem' }}>Rate actually paid on this account (last 90 days)</div>
              <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.5rem', overflow: 'auto' }}>
                <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                  <thead><tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                    <th style={th}>Mode</th><th style={{ ...th, textAlign: 'right' }}>Matches</th><th style={{ ...th, textAlign: 'right' }}>Received</th>
                    <th style={{ ...th, textAlign: 'right' }}>Charges</th><th style={{ ...th, textAlign: 'right' }}>Rate paid</th><th style={{ ...th, textAlign: 'right' }}>Rule rate</th>
                  </tr></thead>
                  <tbody>
                    {stats.length === 0 && <tr><td colSpan={6} style={{ ...td, ...muted, textAlign: 'center' }}>No charges booked through reconciliation yet.</td></tr>}
                    {stats.map(s => {
                      const drift = s.rulePercent != null && Math.abs(s.effectivePercent - s.rulePercent) > Math.max(0.05, s.rulePercent * 0.05);
                      return (
                        <tr key={s.modeCode || 'none'} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={td}>{s.modeName || s.modeCode || 'No mode'}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{s.matches}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{fmt(s.gross)}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{fmt(s.charges)}</td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: drift ? '#d97706' : undefined }}>{fmt(s.effectivePercent, 3)}%{drift && ' ⚠'}</td>
                          <td style={{ ...td, textAlign: 'right', ...muted }}>{s.rulePercent != null ? `${fmt(s.rulePercent, 3)}%` : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {stats.some(s => s.rulePercent != null && Math.abs(s.effectivePercent - s.rulePercent) > Math.max(0.05, s.rulePercent * 0.05)) && (
                <div style={{ fontSize: '0.75rem', color: '#d97706', marginTop: '0.35rem' }}>⚠ The bank is charging a different rate than the rule — update the rule if its pricing changed.</div>
              )}
            </>
          )}

          {tab === 'MODES' && (
            <>
              <div style={{ ...muted, fontSize: '0.78rem', marginBottom: '0.6rem' }}>
                The payment modes receipts and payments record ("Received by" / "Paid by"). Keywords are words the bank prints in the statement
                narration for the mode (e.g. UPI, POS, NEFT) — a hint for the suggestions. Codes never change once created.
              </div>
              {modeForm && (
                <div style={{ background: 'var(--bg-secondary)', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '0.75rem' }}>
                  <div className="form-grid-4">
                    <div><label style={labelStyle}>Code</label>
                      <input className="form-input" value={modeForm.modeCode} disabled={!!modeForm.id}
                        onChange={e => setModeForm({ ...modeForm, modeCode: e.target.value.toUpperCase() })} placeholder="e.g. UPI_QR" style={inputStyle} /></div>
                    <div><label style={labelStyle}>Name</label>
                      <input className="form-input" value={modeForm.modeName} onChange={e => setModeForm({ ...modeForm, modeName: e.target.value })} style={inputStyle} /></div>
                    <div><label style={labelStyle}>Narration keywords (comma-separated)</label>
                      <input className="form-input" value={modeForm.keywordsText} onChange={e => setModeForm({ ...modeForm, keywordsText: e.target.value })} placeholder="UPI, BHIM" style={inputStyle} /></div>
                    <div><label style={labelStyle}>Order</label>
                      <input className="form-input" type="number" value={modeForm.displayOrder} onChange={e => setModeForm({ ...modeForm, displayOrder: Math.round(num(e.target.value)) })} style={inputStyle} /></div>
                  </div>
                  <label style={{ fontSize: '0.8125rem', display: 'inline-flex', gap: '0.35rem', marginTop: '0.4rem' }}>
                    <input type="checkbox" checked={modeForm.isCash} onChange={e => setModeForm({ ...modeForm, isCash: e.target.checked })} /> Cash (no bank reference expected)
                  </label>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <button className="btn btn-secondary" onClick={() => setModeForm(null)} disabled={saving} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleSaveMode} disabled={saving} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>{saving ? 'Saving…' : 'Save mode'}</button>
                  </div>
                </div>
              )}
              <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.5rem', overflow: 'auto', maxHeight: '50vh' }}>
                <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                  <thead><tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                    <th style={th}>Code</th><th style={th}>Name</th><th style={th}>Narration keywords</th><th style={{ ...th, width: 60 }} />
                  </tr></thead>
                  <tbody>
                    {modes.map(m => (
                      <tr key={m.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={td}><strong>{m.modeCode}</strong></td>
                        <td style={td}>{m.modeName}{m.isCash && <span style={muted}> · cash</span>}</td>
                        <td style={{ ...td, ...muted }}>{m.narrationKeywords.join(', ') || '—'}</td>
                        <td style={td}>{canUpdate && (
                          <button className="btn btn-secondary" style={{ fontSize: '0.72rem', padding: '0.15rem 0.45rem' }}
                            onClick={() => setModeForm({ id: m.id, modeCode: m.modeCode, modeName: m.modeName, narrationKeywords: m.narrationKeywords, keywordsText: m.narrationKeywords.join(', '), isCash: m.isCash, displayOrder: m.displayOrder, description: m.description, rowVersion: m.rowVersion })}>
                            <IconSystem.edit size={12} />
                          </button>
                        )}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {canUpdate && !modeForm && (
                <button className="btn btn-secondary" style={{ fontSize: '0.8125rem', padding: '0.35rem 0.75rem', marginTop: '0.6rem' }}
                  onClick={() => setModeForm({ id: null, modeCode: '', modeName: '', narrationKeywords: [], keywordsText: '', isCash: false, displayOrder: 100, description: null, rowVersion: 0 })}>
                  <IconSystem.add size={14} /> Add mode
                </button>
              )}
            </>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Close</button>
        </div>
      </div>
      <ConfirmDialog open={deleteRule !== null} title="Delete Charge Rule"
        message={`Delete the ${deleteRule?.modeName || 'any-mode'} rule${deleteRule?.bankLedgerId ? ' for this account' : ' for all accounts'}? Matches already confirmed keep their charges.`}
        confirmLabel="Delete" cancelLabel="Keep" variant="danger" loading={saving} loadingMessage="Deleting…"
        onConfirm={handleDeleteRule} onCancel={() => setDeleteRule(null)} />
    </>
  );
};

export default ChargeRulesModal;
